import { String } from 'sugar';
import { state } from 'lit/decorators.js';
import { consume } from '@lit/context';
import {
  ltnAuthServiceContext,
  nsDirectoryServiceContext,
  nsWorkflowServiceContext,
} from '../../ns-services/contexts.js';
import { LitElement, PropertyValues, css, nothing } from 'lit';

import { ButtressDbService, ButtressEntity, buttressDbServiceContext } from '@buttress/crag';

import { NSWorkflowService } from '../../ns-workflows/ns-workflow-service.js';
import { LtnAuthService } from '../../ltn-auth/ltn-auth-service.js';
import { NSDirectoryService } from '../../ns-services/ns-directory-service.js';

import { nsSharedStyles, nsCardStyles } from '../../ns-styles/index.js';

import Job from '../../models/job.js';
import type Company from '../../models/company.js';
import JobSchema, { JobOutcomeInputValues } from '../../models/job-schema.js';
import { coerceOutcomeInputs } from './outcome-inputs.js';
import { createLogger } from '../../ns-services/logger.js';
import { jobErrorStore } from '../../ns-services/ns-job-error-store.js';
import { callLambda, lambdaErrorCode } from '../../helpers/lambda.js';
import { jobLedgerGate, noAccountOverlay, waitingOverlay, type JobLedgerGate } from '../../helpers/job-ledger-gate.js';
import { launchLedgerAccount } from '../../helpers/public-ledger-account-api.js';
import { creditsOnTheWay, formatCreditsOnTheWay, type CreditPurchaseProgress } from '../../helpers/credit-pricing.js';
import { ButtressSubscriptions } from '../../helpers/buttress-subscriptions.js';

const logger = createLogger('job');

const DEFAULT_ERROR_MESSAGE = 'Something went wrong, please report the issue to our support to fix it.';

export interface CompletionOverlay {
  title: string;
  paragraphs: string[];
  image?: string;
  buttonText?: string;
}

export interface LedgerValidation {
  company: Company;
  // The registrar's add-company lambda, which validates the company and queues its public ledger message
  lambda: string;
}

export default class NSJobComponent extends LitElement {
  static get is(): string {
    throw new Error(`is not implemented for class ${this.name}`);
  }

  static get schema(): JobSchema {
    throw new Error(`schema not implemented for class ${this.name}`);
  }

  static styles = [
    nsSharedStyles,
    nsCardStyles,
    css`
      :host {
        position: relative;
        display: block;
      }

      .primary-info {
        padding: var(--wa-space-l);
        border-radius: var(--wa-border-radius-l);
        background: var(--ns-color-inverse-surface);
        color: var(--ns-color-inverse-text);
      }

      .primary-info .label {
        color: var(--ns-color-inverse-text);
        text-transform: uppercase;
      }

      .primary-info a {
        color: var(--ns-color-inverse-link);
      }

      .primary-info .info-icon {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 24px;
        height: 24px;
        margin-bottom: var(--wa-space-m);
        border: 1px solid var(--ns-color-inverse-link);
        border-radius: var(--wa-border-radius-circle);
        color: var(--ns-color-inverse-link);
        font-weight: 300;
        line-height: 1;
      }

      .primary-info h3 {
        margin: 0 0 var(--wa-space-m);
        color: var(--ns-color-inverse-text);
        font-size: var(--wa-font-size-l);
        font-weight: 300;
      }

      .primary-info * + h3 {
        margin-top: var(--wa-space-l);
      }

      .primary-info p,
      .primary-info .ticks {
        margin: 0 0 var(--wa-space-m);
      }

      .primary-info > :last-child {
        margin-bottom: 0;
      }

      .primary-info .ticks {
        padding: 0;
        list-style: none;
        font-weight: 300;
      }

      .primary-info .ticks li {
        position: relative;
        padding-left: var(--wa-space-l);
      }

      /* Absolute, so a line break after <li> in the markup doesn't add a space after the tick */
      .primary-info .ticks li::before {
        content: '✓' / '';
        position: absolute;
        left: 0;
      }

      .primary-info .ticks strong {
        color: var(--ns-color-inverse-link);
        font-weight: 500;
      }

      .primary-info .note {
        color: var(--ns-color-inverse-link);
        font-style: italic;
      }

      .job-panel {
        padding: var(--wa-space-l);
        border-radius: var(--ns-card-border-radius, 0.5em);
        background: var(--wa-color-surface-lowered);

        /* Inputs read white on the grey panel; ns-input-field reads its own variable */
        --primary-input-background-color: var(--wa-color-surface-default);
        --ns-input-background: var(--wa-color-surface-default);
        --ns-input-color: var(--wa-color-text-normal);
        --ns-input-placeholder-color: var(--wa-color-text-quiet);
        --ns-detail-list-label-color: var(--wa-color-text-quiet);
      }

      /* The theme's pill radius suits one-line fields only */
      .job-panel wa-textarea {
        --wa-form-control-border-radius: var(--wa-border-radius-l);
      }

      .job-panel + .job-panel {
        margin-top: var(--wa-space-m);
      }

      .job-panel > * {
        margin: 0;
      }

      .job-panel > * + * {
        margin-top: var(--wa-space-m);
      }

      .job-panel h3 {
        font-size: var(--wa-font-size-l);
        font-weight: 500;
      }

      .job-panel p {
        font-weight: 300;
      }

      [slot='intro'] a,
      .job-panel a {
        color: var(--wa-color-text-link);
        font-weight: 500;
      }

      .job-panel .note {
        padding: var(--wa-space-m);
        border-radius: var(--ns-card-border-radius, 0.5em);
        background: var(--wa-color-surface-default);
      }

      .job-option {
        display: flex;
        align-items: center;
        gap: var(--wa-space-s);
        padding: var(--wa-space-xs) var(--wa-space-m);
        border-radius: var(--wa-border-radius-pill);
        background: var(--wa-color-surface-default);
      }

      /* wa-checkbox lays out its host as a column: stretched, its label spans the pill, keeping the box and words at the start */
      wa-checkbox.job-option {
        align-items: stretch;
        justify-content: center;
        padding: 0;
      }

      /* Its label is what toggles it, so it fills the pill and carries the padding: a click anywhere on the row ticks the box */
      wa-checkbox.job-option::part(base) {
        box-sizing: border-box;
        width: 100%;
        padding: var(--wa-space-xs) var(--wa-space-m);
        cursor: pointer;
      }

      .job-option + .job-option {
        margin-top: var(--wa-space-xs);
      }
    `,
  ];

  @consume({ context: buttressDbServiceContext })
  protected _db?: ButtressDbService;

  protected readonly _subscriptions = new ButtressSubscriptions(this, () => this._db);

  @consume({ context: nsWorkflowServiceContext })
  protected _workflowService?: NSWorkflowService;

  @consume({ context: ltnAuthServiceContext })
  protected _auth?: LtnAuthService;

  @consume({ context: nsDirectoryServiceContext })
  protected _directory?: NSDirectoryService;

  protected _job?: Job;

  @state()
  protected _subView: string = 'overview';

  jobTitle?: string;

  async connectedCallback() {
    super.connectedCallback();
    await this.updateComplete;

    if (!this._db) throw new Error('Unable to find ButtressDbService');

    if (!this._workflowService) throw new Error('Unable to find NSWorkflowService');
  }

  get job(): Job {
    if (!this._job) throw new Error(`job not set for ${this.localName}`);
    return this._job;
  }
  set job(value: Job) {
    this._job = value;
  }

  loading(message: string) {
    this.dispatchEvent(new CustomEvent('job-card-loading', { detail: message, bubbles: true, composed: true }));
  }
  changeLoadingMessage(message: string) {
    this.dispatchEvent(
      new CustomEvent('job-card-change-loading-message', {
        detail: message,
        bubbles: true,
        composed: true,
      }),
    );
  }
  loaded() {
    this.dispatchEvent(new CustomEvent('job-card-loading-end', { bubbles: true, composed: true }));
  }
  sign() {
    this.dispatchEvent(new CustomEvent('sign-public-ledger-transaction', { bubbles: true, composed: true }));
  }
  signed() {
    this.dispatchEvent(new CustomEvent('signed-public-ledger-transaction', { bubbles: true, composed: true }));
  }
  confirmCardCompletion() {
    this.dispatchEvent(new CustomEvent('card-confirm-completion', { bubbles: true, composed: true }));
  }

  openCompletionOverlay({
    title,
    paragraphs,
    image = '/images/icons/overlay-tick.svg',
    buttonText = 'I understand',
  }: CompletionOverlay) {
    this.dispatchEvent(
      new CustomEvent('open-overlay', {
        detail: { title, paragraphs, image, buttonText },
        bubbles: true,
        composed: true,
      }),
    );
    this.confirmCardCompletion();
    this.classList.add('confirm-completion');
  }

  // The lambda records the validation and queues its message on nodeStream's INTERNAL public ledger topic, which nodeStream
  // sends and pays for: nobody signs it
  async recordLedgerValidation({ company, lambda }: LedgerValidation) {
    if (!this._auth) throw new Error('Missing auth service');

    await callLambda(lambda, 'POST', this._auth.token, JSON.stringify({ companyId: company.id, jobId: this.job.id }));
  }

  triggerCardError(message: string) {
    this.dispatchEvent(new CustomEvent('job-card-error-message', { detail: message, bubbles: true, composed: true }));
  }

  clearCardError() {
    this.dispatchEvent(new CustomEvent('job-card-error-message', { detail: undefined, bubbles: true, composed: true }));
  }

  fatalError(message: string) {
    this.dispatchEvent(new CustomEvent('job-card-fatal-error', { detail: message, bubbles: true, composed: true }));
  }

  showError(err: unknown, fallback = DEFAULT_ERROR_MESSAGE) {
    const message = typeof err === 'string' ? err : err instanceof Error && err.message ? err.message : fallback;
    if (this._job) jobErrorStore.set(this._job.id, message);
    this.triggerCardError(message);
    return message;
  }

  clearError() {
    if (this._job) jobErrorStore.clear(this._job.id);
    this.clearCardError();
  }

  failJob(err: unknown, fallback: string) {
    if (err instanceof Error) return this.fatalError(err.message);

    this.fatalError(fallback);
    throw err;
  }

  // Check to see if the current class has a handler function, if not call outcome directly.
  async outcomeHandler(key: string) {
    const fnKey = String.camelize(key, true);

    if (typeof (this as any)[`_outcome${fnKey}Handler`] === 'function') {
      await (this as any)[`_outcome${fnKey}Handler`].call(this);
    } else {
      await this.outcome(key);
    }
  }

  // Invitation keys land on the job's workflow; the lambda decides which by key
  async updateMetadata(key: string, value: string) {
    if (!this._auth) throw new Error('Missing auth service');
    if (!this._job) throw new Error('Unable to find job object');
    if (!key) throw new Error('Missing required metadata key');
    if (!value) throw new Error('Missing required metadata value');

    const { res } = await callLambda(
      'job/update-metadata',
      'POST',
      this._auth.token,
      JSON.stringify({ jobId: this._job.id, key, value }),
    );
    if (res.code !== 200) throw new Error(typeof res.message === 'string' ? res.message : 'Unable to update the job');
  }

  async loadInput<T extends ButtressEntity>(collection: string, inputKey: string): Promise<T> {
    if (!this._db) throw new Error('Unable to find db service');

    const id = this.job.inputValues?.[inputKey];
    if (!id || typeof id !== 'string') throw new Error(`Missing required ${inputKey} on this job`);

    const entity = await this._db.getById<T>(collection, id);
    if (!entity) throw new Error(`Unable to find ${collection} with ID: ${id}`);

    return entity;
  }

  // The company the workflow put this job in (the node's set.companyId, or the workflow's)
  async loadJobCompany<T extends ButtressEntity>(): Promise<T> {
    if (!this._db) throw new Error('Unable to find db service');
    if (!this.job.companyId) throw new Error('Missing required company on this job');

    const company = await this._db.getById<T>('company', this.job.companyId);
    if (!company) throw new Error(`Unable to find company with ID: ${this.job.companyId}`);

    return company;
  }

  getMetadataValue(key: string) {
    if (!this._db) return logger.warn('Unable to find db service');
    if (!this._job) return logger.warn('Unable to find job object');
    if (!key) return logger.warn('Missing required metadata key');

    const metadataIdx = this._job.metadata.findIndex((m) => m.key === key);
    if (metadataIdx === -1) return logger.warn(`Could not find a metadata with key ${key} in the job`);

    const metadata = this._job.metadata[metadataIdx];
    if (!metadata || !metadata.value) return logger.warn(`The metadata with key ${key} missing a metadata value`);

    return metadata.value;
  }

  async outcome(key: string, inputs?: JobOutcomeInputValues) {
    if (!this._job) throw new Error('Unable to find job');
    if (!this._workflowService) throw new Error('Unable to find NSWorkflowService');

    const schema = Object.getPrototypeOf(this).constructor.schema as JobSchema;

    // Check that the outcome exists on this job.
    const outcome = schema.outcomes[key];
    if (!outcome) throw new Error('Unknown job outcome');

    const output = coerceOutcomeInputs(outcome.inputs, inputs);

    try {
      await this._workflowService.jobOutcome(this._job.id, key, output);

      this._jobComplete();
    } catch (err) {
      // An outcome that declares a public ledger message can be refused by job/complete's checks (stage 9); the job stays
      // open, and the card says why before the refusal goes on to the caller
      const gate = jobLedgerGate(lambdaErrorCode(err), err instanceof Error ? err.message : null);
      if (gate) await this.__showLedgerGate(gate);
      throw err;
    }
  }

  // Credits owed (D14): the credits on the way, with "I understand"; the job doesn't complete and the person tries again
  // later (OQ3). No public ledger account (D7, OQ6): their create-account workflow is started or found, with a way to it.
  // Waiting for automatic payments, funds, next month and the rest (#25 phase 5): "Waiting for …", why, and that they'll be
  // notified to complete it again, with "I understand"; the job stays open. A pending signatory change or a low allowance
  // (OQ3), or (#25 phase 6) a target raise the signatories declined: the refusal's message on the card.
  private async __showLedgerGate(gate: JobLedgerGate) {
    if (gate.state === 'NO_LEDGER_ACCOUNT') {
      await this.__showNoAccount(gate);
      return;
    }
    const waiting = waitingOverlay(gate);
    if (waiting) {
      this.dispatchEvent(
        new CustomEvent('open-overlay', {
          detail: { ...waiting, image: '/images/icons/time-icon.svg' },
          bubbles: true,
          composed: true,
        }),
      );
      return;
    }
    if (gate.state !== 'CREDITS_ON_THE_WAY') {
      this.showError(gate.message);
      return;
    }

    const figures = await this.__creditsOnTheWay();
    this.dispatchEvent(
      new CustomEvent('open-overlay', {
        detail: {
          title: 'The rest of your credits are on their way',
          paragraphs: [...(figures ? [formatCreditsOnTheWay(figures)] : []), gate.message],
          image: '/images/icons/time-icon.svg',
          buttonText: 'I understand',
        },
        bubbles: true,
        composed: true,
      }),
    );
  }

  private async __showNoAccount(gate: JobLedgerGate) {
    if (!this._job) return;
    let launch;
    try {
      launch = await launchLedgerAccount(this._auth?.token, this._job.id);
    } catch (err) {
      logger.warn('Unable to start the public ledger account', err);
      this.showError(err, gate.message);
      return;
    }
    this.dispatchEvent(
      new CustomEvent('open-overlay', {
        detail: { ...noAccountOverlay(gate, launch), image: '/images/icons/time-icon.svg' },
        bubbles: true,
        composed: true,
      }),
    );
  }

  // The company's purchases still owed, as members may read them; null when they can't be read
  private async __creditsOnTheWay() {
    if (!this._db || !this._job?.companyId) return null;
    try {
      const found = await this._db.query('plCreditPurchase', { companyId: { $eq: this._job.companyId } });
      return creditsOnTheWay((found.results ?? []) as unknown as CreditPurchaseProgress[]);
    } catch (err) {
      logger.warn('Unable to read the credits on the way', err);
      return null;
    }
  }

  protected _jobComplete() {
    this.dispatchEvent(
      new CustomEvent('job-complete', {
        // detail: entity,
        bubbles: true,
        cancelable: false,
        composed: true,
      }),
    );
  }

  switchView(view: string) {
    this._subView = view;
  }

  // The job's own buttons for the card's bottom bar; arrow handlers only, as the container renders them
  renderActions(): unknown {
    return nothing;
  }

  // The bar is the container's render, so it re-renders whenever the job does
  protected updated(changedProperties: PropertyValues) {
    super.updated(changedProperties);
    this.dispatchEvent(new CustomEvent('job-actions-change'));
  }
}
